import React from "react";
import Button from "@mui/material/Button";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";

export default function FiltersButtons({
  hideDepletedAssets,
  hasReservedQuantity,
  showReservedFilter,
  warehouses = [],
  selectedWarehouseIds = [],
  costCenters = [],
  selectedCostCenterIds = [],
  hideCostCenterFilter = false,
  onChange,
}) {
  const handleAvailabilityChange = (mode) => {
    onChange({ availabilityMode: mode });
  };

  const handleWarehouseChange = (warehouseId) => {
    const warehouseIds = selectedWarehouseIds.includes(warehouseId)
      ? selectedWarehouseIds.filter((id) => id !== warehouseId)
      : [...selectedWarehouseIds, warehouseId];
    onChange({ warehouseIds, page: 1 });
  };

  const handleCostCenterChange = (costCenterId) => {
    const costCenterIds = selectedCostCenterIds.includes(costCenterId)
      ? selectedCostCenterIds.filter((id) => id !== costCenterId)
      : [...selectedCostCenterIds, costCenterId];
    onChange({ costCenterIds, page: 1 });
  };

  const handleClearFilters = () => {
    const updates = { availabilityMode: "in_stock", costCenterIds: [] };
    if (warehouses.length > 1) {
      updates.warehouseIds = [];
    }
    onChange(updates);
  };

  return (
    <Box>
      <PopupState variant="popover" popupId="filters-popup">
        {(popupState) => (
          <>
            <Button
              variant="outlined"
              startIcon={<FilterAltOutlinedIcon />}
              endIcon={<KeyboardArrowDownIcon />}
              {...bindTrigger(popupState)}
              sx={{ mr: 1.5 }}
            >
              Filters
            </Button>
            <Menu
              {...bindMenu(popupState)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              sx={{ mt: 0.5 }}
              PaperProps={{ sx: { width: 380 } }}
            >
              <Typography fontSize={18} color="textPrimary" sx={{ p: 2 }}>
                Inventory Filters
              </Typography>
              <MenuItem
                onClick={() => {
                  handleAvailabilityChange("in_stock");
                  popupState.close();
                }}
              >
                <FormControlLabel
                  control={
                    <Radio checked={hideDepletedAssets && !hasReservedQuantity} sx={{ m: 0 }} />
                  }
                  label="Available Now"
                  sx={{ width: "100%", mb: 0, "& .MuiFormControlLabel-root": { mb: 0 } }}
                />
              </MenuItem>
              {showReservedFilter && (
                <MenuItem
                  onClick={() => {
                    handleAvailabilityChange("reserved");
                    popupState.close();
                  }}
                >
                  <FormControlLabel
                    control={<Radio checked={hasReservedQuantity} sx={{ m: 0 }} />}
                    label="Reserved"
                    sx={{ width: "100%", mb: 0, "& .MuiFormControlLabel-root": { mb: 0 } }}
                  />
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  handleAvailabilityChange("all");
                  popupState.close();
                }}
              >
                <FormControlLabel
                  control={
                    <Radio checked={!hideDepletedAssets && !hasReservedQuantity} sx={{ m: 0 }} />
                  }
                  label="All Items"
                  sx={{ width: "100%", mb: 0, "& .MuiFormControlLabel-root": { mb: 0 } }}
                />
              </MenuItem>

              {warehouses.length > 1 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography fontSize={18} color="textPrimary" sx={{ p: 2 }}>
                    Warehouse Filters
                  </Typography>
                  <MenuItem
                    onClick={() => {
                      onChange({ warehouseIds: [], page: 1 });
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox checked={selectedWarehouseIds.length === 0} sx={{ m: 0 }} />
                      }
                      label="All Warehouses"
                      sx={{ width: "100%", mb: 0, "& .MuiFormControlLabel-root": { mb: 0 } }}
                    />
                  </MenuItem>
                  {warehouses.map((warehouse) => (
                    <MenuItem
                      key={warehouse.warehouseId}
                      onClick={() => {
                        handleWarehouseChange(warehouse.warehouseId);
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedWarehouseIds.includes(warehouse.warehouseId)}
                            sx={{ m: 0 }}
                          />
                        }
                        label={warehouse.name}
                        sx={{ width: "100%", mb: 0, "& .MuiFormControlLabel-root": { mb: 0 } }}
                      />
                    </MenuItem>
                  ))}
                </>
              )}
              {!hideCostCenterFilter && costCenters.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography fontSize={18} color="textPrimary" sx={{ p: 2 }}>
                    Cost Center Filters
                  </Typography>
                  <MenuItem onClick={() => onChange({ costCenterIds: [], page: 1 })}>
                    <FormControlLabel
                      control={
                        <Checkbox checked={selectedCostCenterIds.length === 0} sx={{ m: 0 }} />
                      }
                      label="All Cost Centers"
                      sx={{ width: "100%", mb: 0, "& .MuiFormControlLabel-root": { mb: 0 } }}
                    />
                  </MenuItem>
                  {costCenters.map((costCenter) => {
                    const costCenterId = String(costCenter.id);
                    const label = `${costCenter.costCenterName || ""}${
                      costCenter.costCenterCode ? ` ${costCenter.costCenterCode}` : ""
                    }`.trim();

                    return (
                      <MenuItem
                        key={costCenterId}
                        onClick={() => handleCostCenterChange(costCenterId)}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedCostCenterIds.includes(costCenterId)}
                              sx={{ m: 0 }}
                            />
                          }
                          label={label}
                          sx={{ width: "100%", mb: 0, "& .MuiFormControlLabel-root": { mb: 0 } }}
                        />
                      </MenuItem>
                    );
                  })}
                </>
              )}
            </Menu>
          </>
        )}
      </PopupState>
      {(!hideDepletedAssets ||
        hasReservedQuantity ||
        selectedWarehouseIds.length > 0 ||
        selectedCostCenterIds.length > 0) && (
        <Button variant="contained" color="primary" onClick={handleClearFilters}>
          Clear Filters
        </Button>
      )}
    </Box>
  );
}
